/**
 * Department Timesheet Access API
 * Handles department-level timesheet permission management
 */
import apiClient from './apiClient';

export interface DepartmentTimesheetAccess {
  id: number;
  user_id: number;
  user_name?: string;
  department_id: number;
  department_name?: string;
  can_view: boolean;
  can_approve: boolean;
  can_reject: boolean;
  can_update: boolean;
  granted_by?: number;
  granted_by_name?: string;
  granted_at?: string;
  updated_at?: string;
  notes?: string;
}

export interface DepartmentForAccess {
  id: number;
  name: string;
  is_active: boolean;
}

export interface DepartmentAccessAssignment {
  department_id: number;
  can_view: boolean;
  can_approve: boolean;
  can_reject: boolean;
  can_update: boolean;
  notes?: string;
}

export interface GetAllAccessResponse {
  access_records: DepartmentTimesheetAccess[];
  total: number;
}

export interface GetUserAccessResponse {
  user_id: number;
  user_name: string;
  access_records: DepartmentTimesheetAccess[];
  total: number;
}

export interface GetDepartmentsResponse {
  departments: DepartmentForAccess[];
}

export interface UpdateUserAccessData {
  assignments: DepartmentAccessAssignment[];
}

export interface UpdateUserAccessResponse {
  message: string;
  user_id: number;
  created: string[];
  updated: string[];
  removed: string[];
  access_records: DepartmentTimesheetAccess[];
}

export const departmentTimesheetAccessApi = {
  /**
   * Get all department timesheet access assignments (SuperAdmin only)
   */
  getAllAccess: async (params?: { user_id?: number; department_id?: number }): Promise<GetAllAccessResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.user_id) queryParams.append('user_id', params.user_id.toString());
    if (params?.department_id) queryParams.append('department_id', params.department_id.toString());
    const queryString = queryParams.toString();
    return apiClient.get<GetAllAccessResponse>(`/department-timesheet-access${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get all departments for the access assignment dropdown
   */
  getDepartments: async (): Promise<GetDepartmentsResponse> => {
    return apiClient.get<GetDepartmentsResponse>('/department-timesheet-access/departments');
  },

  /**
   * Get a user's department timesheet access assignments
   */
  getUserAccess: async (userId: number): Promise<GetUserAccessResponse> => {
    return apiClient.get<GetUserAccessResponse>(`/department-timesheet-access/user/${userId}`);
  },

  /**
   * Update a user's department timesheet access (SuperAdmin only)
   * Upserts assignments - creates new ones, updates existing, removes ones with all permissions false
   */
  updateUserAccess: async (userId: number, data: UpdateUserAccessData): Promise<UpdateUserAccessResponse> => {
    return apiClient.put<UpdateUserAccessResponse>(`/department-timesheet-access/user/${userId}`, data);
  },

  /**
   * Remove a specific department access for a user
   */
  removeUserDepartmentAccess: async (userId: number, departmentId: number): Promise<{ message: string }> => {
    return apiClient.delete<{ message: string }>(`/department-timesheet-access/user/${userId}/department/${departmentId}`);
  },

  /**
   * Reset all department access for a user (remove all assignments)
   */
  resetUserAccess: async (userId: number): Promise<{ message: string; removed_count: number }> => {
    return apiClient.post<{ message: string; removed_count: number }>(`/department-timesheet-access/user/${userId}/reset`);
  },
};

export default departmentTimesheetAccessApi;
